import AccessibleFormValidator from './AccessibleFormValidator';
import PreloaderClass from './PreloaderClass';
import PostHogClass from './PostHogClass';
import { fetchRequestSend, sendEventToAnalytics } from '../../helpers/common-helpers';

/**
 * Base for the website's trial-signup forms (contact form + signup form).
 * Validation + accessibility live in AccessibleFormValidator; this owns the
 * shared submission flow (validate → fetch → identify → capture → redirect).
 *
 * Subclasses pass a config via super():
 *   - formId         id of the <form> to bind (required)
 *   - analyticsEvent PostHog event fired on successful submit (required)
 *   - openEvent      PostHog event fired once when the form is present (optional)
 *
 * The form must carry `data-url` (submit endpoint); `data-redirect` is optional
 * and overrides the server-returned redirect.
 */
export default class TrialFormBase {
    constructor(config) {
        this.form = null;
        this.trialUrl = null;
        this.validator = null;
        this.config = config;

        // Bindings
        this.$handleSubmit = this.handleSubmit.bind(this);

        this.init();
    }

    /**
     * Field-specific rules, keyed by field `name`. Override to extend.
     * The full-name rule mirrors the backend (TrialController): the name must
     * contain a first AND last name, i.e. at least two space-separated parts.
     */
    customValidators() {
        return {
            customer_name: (value, { messages }) =>
                value.split(/\s+/).length < 2 ? messages.fullName : null
        };
    }

    init() {
        const { formId, openEvent } = this.config;
        this.form = document.getElementById(formId);

        if (!this.form) {
            return;
        }

        this.trialUrl = this.form.dataset.url;
        this.validator = new AccessibleFormValidator(this.form, {
            customValidators: this.customValidators()
        });

        this.form.addEventListener('submit', this.$handleSubmit);
        if (openEvent) {
            sendEventToAnalytics(openEvent);
        }
    }

    handleSubmit(e) {
        e.preventDefault();
        PreloaderClass.forPopup(this.form.id);

        if (!this.validator.validate()) {
            PreloaderClass.stop();
            return;
        }

        this.createTrialAccount();
    }

    createTrialAccount() {
        if (!this.trialUrl) {
            PreloaderClass.stop();
            return;
        }

        const formData = new FormData(this.form);

        fetchRequestSend(this.trialUrl, 'POST', formData)
            .then(response => {
                const { status, redirect_url, fields, errors } = response;

                // Work-email/domain enforcement lives on the backend
                // (TrialController + logo.dev); it comes back as a field error.
                if (!status) {
                    this.validator.showServerErrors(fields, errors);
                    return;
                }

                this.trackSubmission(formData);
                this.validator.clearErrors();

                const redirectUrl = this.form.dataset.redirect || redirect_url;
                window.location.href = `${redirectUrl}&demo=take-course`;
            })
            .catch(error => {
                console.error(`Trial form (${this.config.formId}) submission failed:`, error);
            })
            .finally(() => {
                PreloaderClass.stop();
            });
    }

    trackSubmission(formData) {
        const { analyticsEvent } = this.config;

        // PostHog: identify the now-known user first.
        // With person_profiles: 'identified_only', identify() creates the person
        // profile, so it must run BEFORE capture — otherwise the submit event is
        // anonymous and may not stitch cleanly to the lead.
        PostHogClass.identify(formData.get('email'));

        // Then send the submit event with the collected user data (email, name,
        // job title, and any other fields the form carries).
        // sendBeacon so the event survives the redirect (a PostHog webhook -> n8n triggers off it).
        sendEventToAnalytics(analyticsEvent, PostHogClass.userPropsFromFormData(formData), { transport: 'sendBeacon' });
    }
}